import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {useClipboard} from '../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {newPalette} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {AiReview, computeAiReviewStatus} from '../../../../../helpers/models/conversationAiHelpers';
import {AnnotationAiKnowledgeBaseArticlesSuggestionModel} from '../../../../../models/api/annotationModel';
import {ConversationAiStatusesEnum} from '../../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';

/*
 * Props.
 */

interface AiKnowledgeBaseArticlesSuggestionCommentBodyProps {
  conversation: ConversationModel | undefined;
  aiSuggestion: AnnotationAiKnowledgeBaseArticlesSuggestionModel;
  onAiReview: (review: AiReview) => void;
}

/*
 * Style.
 */

const StyledCommentDiv = styled.div`
  padding: 8px;
  line-height: 22px;
  display: grid;
  grid-row-gap: 8px;
`;

const StyledKbArticlePreviewDiv = styled.div`
  padding: 5px 8px 5px 12px;
  border-radius: 8px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 3px;

  max-width: 400px;

  font-weight: ${fontWeights.medium};

  background-color: ${(p) => p.theme.foreground};
`;

const StyledIcon = styled(Icon)`
  margin-right: 2px;
`;

const StyledKbArticleNameDiv = styled.div`
  ${ellipsis()}
`;

const StyledButtonsWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

/*
 * Component.
 */

export const AiKnowledgeBaseArticlesSuggestionCommentBody: FC<
  AiKnowledgeBaseArticlesSuggestionCommentBodyProps
> = (props) => {
  const {conversation, aiSuggestion, onAiReview} = props;

  const theme = useTheme();
  const {copy} = useClipboard();

  return (
    <StyledCommentDiv>
      {aiSuggestion.articles.map(({articleId, articleName, articleUrl}) => (
        <StyledKbArticlePreviewDiv key={articleId}>
          <StyledIcon name="articleSmall" />
          {renderArticleName(articleName)}
          <StyledButtonsWrapperDiv>
            {renderControlButtons(conversation, aiSuggestion, onAiReview, articleId)}
            <IconButton
              isRounded
              iconName="externalLinkSmall"
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              label={<Trans id="ai-knowledge-base-article-suggestion-open">Open in browser</Trans>}
              onClick={() => openInBrowser(articleUrl)}
            />
            <IconButton
              isRounded
              iconName="copySmall"
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              label={<Trans id="ai-knowledge-base-article-suggestion-copy">Copy link</Trans>}
              onClick={() => copy(articleUrl)}
            />
          </StyledButtonsWrapperDiv>
        </StyledKbArticlePreviewDiv>
      ))}
    </StyledCommentDiv>
  );
};

/*
 * Helpers.
 */

function renderArticleName(articleName: string) {
  return (
    <TooltipCoordinator render={() => articleName}>
      <StyledKbArticleNameDiv>
        <TooltipOverflowCondition>{articleName}</TooltipOverflowCondition>
      </StyledKbArticleNameDiv>
    </TooltipCoordinator>
  );
}

function renderControlButtons(
  conversation: ConversationModel | undefined,
  aiSuggestion: AnnotationAiKnowledgeBaseArticlesSuggestionModel,
  onAiReview: (review: AiReview) => void,
  resourceId: number,
) {
  const aiStatus = computeAiReviewStatus(conversation, resourceId, aiSuggestion);

  switch (aiStatus) {
    case ConversationAiStatusesEnum.APPROVED:
      return (
        <IconButton
          isRounded
          iconName="checkmarkCircleSmall"
          isDisabled
          label={<Trans id="ai-knowledge-base-article-suggestion-approved">Accepted</Trans>}
        />
      );

    case ConversationAiStatusesEnum.DISCARDED:
      return (
        <IconButton
          isRounded
          iconName="crossCircleSmall"
          isDisabled
          label={<Trans id="ai-knowledge-base-article-suggestion-discarded">Rejected</Trans>}
        />
      );

    default:
      return (
        <>
          <IconButton
            isRounded
            iconName="checkmarkCircleSmall"
            color={newPalette.green.lowlight}
            hoverColor={newPalette.green.lowlight}
            label={<Trans id="ai-knowledge-base-article-suggestion-approve">Accept</Trans>}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.APPROVED,
                resourceId,
              })
            }
          />

          <IconButton
            isRounded
            iconName="crossCircleSmall"
            color={newPalette.red.lowlight}
            hoverColor={newPalette.red.lowlight}
            label={<Trans id="ai-knowledge-base-article-suggestion-discard">Reject</Trans>}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.DISCARDED,
                resourceId,
              })
            }
          />
        </>
      );
  }
}
